<template>
  <AdminLayout>
  <div class="d-flex flex-column">
    <div class="d-flex flex-row align-center mb-3">
      <h1 class="outfit-regular">Conundrums</h1>
      <v-spacer></v-spacer>
    </div>
    <v-progress-linear :value="progressValue" stream color="yellow darken-3" class="mt-2 mb-2"></v-progress-linear>

    <v-tabs v-model="tab" background-color="transparent">
      <v-tabs-slider color="black"></v-tabs-slider>
      <v-tab>Dashboard</v-tab>
      <v-tab>Generate</v-tab>
      <v-tab>Review</v-tab>
      <v-tab>Evaluation</v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">

      <!-- Dashboard Tab -->
      <v-tab-item>
        <v-sheet class="pa-6" rounded color="grey lighten-4">
          <div class="d-flex flex-column">
            <div class="d-flex flex-row flex-wrap">
              <v-card class="pa-4 mr-4 mb-4" outlined>
                <div class="subtitle-2 grey--text">Processing</div>
                <div class="display-1">{{ processingCount }}</div>
              </v-card>
              <v-card class="pa-4 mr-4 mb-4" outlined style="min-width:320px">
                <div class="subtitle-2 grey--text mb-2">Top Prompts</div>
                <div v-if="topPrompts.length===0" class="caption grey--text">No data</div>
                <div v-for="p in topPrompts" :key="p.key" class="mb-2">
                  <div class="font-weight-medium" style="max-width:540px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">{{ p.prompt }}</div>
                  <div class="caption grey--text">avg rating {{ p.avgRating.toFixed(1) }} · n={{ p.count }}</div>
                </div>
              </v-card>
              <v-card class="pa-4 mr-4 mb-4" outlined style="min-width:280px">
                <div class="subtitle-2 grey--text mb-2">Top Models</div>
                <div v-if="topModels.length===0" class="caption grey--text">No data</div>
                <div v-for="m in topModels" :key="m.key" class="mb-2">
                  <div class="font-weight-medium">{{ m.label }}</div>
                  <div class="caption grey--text">avg rating {{ m.avgRating.toFixed(1) }} · n={{ m.count }}</div>
                </div>
              </v-card>
            </div>

            <v-card class="pa-4" outlined>
              <div class="d-flex flex-row align-center mb-3">
                <div class="cal-sans-regular font-size-20">All Conundrums</div>
                <v-spacer></v-spacer>
                <v-text-field v-model="allSearch" dense hide-details prepend-inner-icon="mdi-magnify" label="Search" outlined style="max-width: 320px"></v-text-field>
              </div>
              <v-simple-table>
                <thead>
                  <tr>
                    <th class="text-left" style="width:80px">Rating</th>
                    <th class="text-left">Title</th>
                    <th class="text-left">Prompt</th>
                    <th class="text-left" style="width:160px">Model</th>
                    <th class="text-left" style="width:120px">State</th>
                    <th class="text-left" style="width: 220px">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in allConundrumsFiltered" :key="c.id">
                    <td>
                      <v-text-field :disabled="!isAdmin" v-model.number="c.rating" type="number" min="1" max="10" dense hide-details style="max-width: 72px" @change="saveRating(c)"></v-text-field>
                    </td>
                    <td class="py-3"><strong>{{ c.title }}</strong></td>
                    <td class="py-3" style="max-width: 520px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">{{ c.prompt || c.promptUsed }}</td>
                    <td class="py-3">{{ modelLabel(c) }}</td>
                    <td class="py-3">{{ c.state }}</td>
                    <td class="py-3">
                      <v-btn small class="mr-2" @click="preview(c)">Preview</v-btn>
                      <v-btn small color="green" dark class="mr-2" v-if="c.state!=='public' && (isAdmin || c?.creator?.id === ($root?.user?.uid||''))" @click="publish(c)">Publish</v-btn>
                      <v-btn small color="blue" dark class="mr-2" v-if="isAdmin" @click="reEvaluate(c)">Re-eval</v-btn>
                      <v-btn small color="deep-purple" dark v-if="isAdmin" @click="rewrite(c)">Rewrite</v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card>

            <v-dialog v-model="show" max-width="800">
              <v-card>
                <v-card-title class="headline">{{ current?.title }}</v-card-title>
                <v-card-text>
                  <div class="mb-2">{{ current?.description }}</div>
                  <div class="mb-4">{{ current?.prompt }}</div>
                  <div>
                    <v-chip v-for="o in current?.options || []" :key="o.id" class="ma-1">{{ o.text }}</v-chip>
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="show=false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </div>
        </v-sheet>
      </v-tab-item>

      <!-- Generate Tab -->
      <v-tab-item>
        <v-sheet class="pa-6" rounded color="grey lighten-4">
          <div class="d-flex flex-row">
            <div class="d-flex flex-1 flex-column mr-4">
              <div class="subtitle-1">Seed Prompt</div>
              <v-textarea v-model="seedPrompt" solo outlined rows="6" :loading="suggesting" :disabled="!isAdmin"></v-textarea>
              <div class="d-flex flex-row mt-2">
                <v-text-field v-model.number="genCount" type="number" min="1" max="50" label="Count" solo outlined class="flex-1 mr-2" :disabled="!isAdmin"></v-text-field>
                <v-select :items="providers" v-model="genProvider" label="Provider" solo outlined class="flex-1 mr-2" :disabled="!isAdmin"></v-select>
                <v-select :items="tiers" v-model="genTier" label="Tier" solo outlined class="flex-1" :disabled="!isAdmin"></v-select>
              </div>
              <div class="d-flex flex-row mt-2">
                
                <v-btn v-if="isAdmin" color="black" dark class="elevation-0 mr-2" :loading="generating" @click="createUnprocessed">
                  <v-icon left>mdi-queue</v-icon> Queue
                </v-btn>
                <v-btn v-if="isAdmin" color="yellow darken-3" class="elevation-0" :loading="suggesting" @click="suggestSeed">
                  <v-icon left>mdi-lightbulb-on</v-icon> Suggest Prompt
                </v-btn>
              </div>
            </div>
            <div class="d-flex flex-1 flex-column">
              <div class="subtitle-1 mb-2">Unprocessed Queue</div>
              <div class="d-flex flex-row align-center mb-2">
                <v-text-field v-model="unprocessedSearch" hide-details prepend-inner-icon="mdi-magnify" label="Search" solo outlined dense></v-text-field>
              </div>
              <div class="pt-2 pb-3">
                <v-btn v-if="isAdmin" color="black" dark small :loading="processingNow" @click="processUnprocessedNow">Process 5 now</v-btn>
              </div>
              <v-card>
                <v-simple-table>
                  <thead>
                    <tr>
                      <th class="text-left" style="width:160px">Status</th>
                      <th class="text-left">Prompt</th>
                      <th class="text-left" style="width:160px">Model</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="q in unprocessedFiltered" :key="q.id">
                      <td>
                        <v-chip small :color="statusColor(q.processing?.status)" dark>{{ q.processing?.status || 'unprocessed' }}</v-chip>
                      </td>
                      <td class="py-2" style="max-width:520px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">{{ q.promptUsed || q.prompt }}</td>
                      <td>{{ modelLabel(q) }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card>
            </div>
          </div>
        </v-sheet>
      </v-tab-item>

      <!-- Review Tab -->
      <v-tab-item>
        <v-sheet class="pa-6" rounded color="grey lighten-4">
          <div class="d-flex flex-column">
            <!-- Large review card -->
            <v-card class="mt-4 pa-4" outlined>
              <div v-if="currentReview">
                <div class="d-flex flex-row align-center mb-2">
                  <div class="cal-sans-regular font-size-20">{{ currentReview.title || 'Untitled' }}</div>
                  <v-spacer></v-spacer>
                  <v-chip small color="blue" dark>Eval: {{ currentReview.evaluation?.score || 0 }}</v-chip>
                </div>
                <div class="mb-2 grey--text" v-if="currentReview.description">{{ currentReview.description }}</div>
                <div class="mb-3" style="white-space: pre-wrap;">{{ currentReview.prompt }}</div>
                <div class="mb-4">
                  <div class="subtitle-2 grey--text mb-1">Options</div>
                  <div class="d-flex flex-column">
                    <v-chip
                      v-for="o in (currentReview.options||[])"
                      :key="o.id"
                      class="ma-1"
                      outlined
                      large
                    >{{ o.text }}</v-chip>
                  </div>
                </div>
                <div class="d-flex flex-row align-center">
                  <div class="subtitle-2 grey--text mr-3">Rate</div>
                  <v-rating
                    v-model.number="currentReview.rating"
                    :length="5"
                    background-color="yellow darken-3"
                    color="yellow darken-3"
                    large
                    @input="rateCurrent(currentReview.rating)"
                  ></v-rating>
                  <v-spacer></v-spacer>
                  <v-btn small class="mr-2" @click="skipCurrent">Skip</v-btn>
                  <v-btn small color="deep-purple" dark class="mr-2" @click="rewrite(currentReview)">Rewrite</v-btn>
                  <v-btn small color="blue" dark @click="reEvaluate(currentReview)">Re-eval</v-btn>
                </div>
              </div>
              <div v-else class="grey--text">No unreviewed conundrums match your search.</div>
            </v-card>

            <div class="d-flex flex-row align-center mt-4">
              <v-text-field v-model="reviewSearch" full-width hide-details prepend-inner-icon="mdi-magnify" label="Search to review" solo outlined></v-text-field>
              <v-spacer></v-spacer>
              <v-btn icon small @click="loadReview">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </div>
            <!-- Bottom tabs: Unreviewed / Reviewed lists -->
            <v-card class="mt-4" outlined>
              <v-tabs v-model="reviewBottomTab" background-color="transparent">
                <v-tabs-slider color="black"></v-tabs-slider>
                <v-tab>Unreviewed ({{ unreviewedFiltered.length }})</v-tab>
                <v-tab>Reviewed ({{ reviewedFiltered.length }})</v-tab>
              </v-tabs>
              <v-tabs-items v-model="reviewBottomTab">
                <v-tab-item>
                  <v-simple-table>
                    <thead>
                      <tr>
                        <th class="text-left">Title</th>
                        <th class="text-left">Prompt</th>
                        <th class="text-left" style="width:120px">Eval</th>
                        <th class="text-left" style="width:120px">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="c in unreviewedFiltered" :key="c.id">
                        <td class="py-2"><strong>{{ c.title }}</strong></td>
                        <td class="py-2" style="max-width: 560px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">{{ c.prompt }}</td>
                        <td class="py-2"><v-chip x-small>{{ c.evaluation?.score || 0 }}</v-chip></td>
                        <td class="py-2"><v-btn small @click="setCurrent(c)">Open</v-btn></td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-tab-item>
                <v-tab-item>
                  <v-simple-table>
                    <thead>
                      <tr>
                        <th class="text-left">Title</th>
                        <th class="text-left">Prompt</th>
                        <th class="text-left" style="width:80px">Rating</th>
                        <th class="text-left" style="width:120px">Eval</th>
                        <th class="text-left" style="width:180px">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="c in reviewedFiltered" :key="c.id">
                        <td class="py-2"><strong>{{ c.title }}</strong></td>
                        <td class="py-2" style="max-width: 560px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">{{ c.prompt }}</td>
                        <td class="py-2">{{ c.rating }}</td>
                        <td class="py-2"><v-chip x-small>{{ c.evaluation?.score || 0 }}</v-chip></td>
                        <td class="py-2">
                          <v-btn x-small class="mr-2" @click="setCurrent(c)">Open</v-btn>
                          <v-btn x-small color="green" dark @click="publish(c)">Publish</v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-tab-item>
              </v-tabs-items>
            </v-card>
          </div>
        </v-sheet>
      </v-tab-item>

      <!-- Evaluation Tab -->
      <v-tab-item>
        <v-sheet class="pa-6" rounded color="grey lighten-4">
          <div class="d-flex flex-column">
            <div class="subtitle-1 mb-2">Evaluation Prompt Builder</div>
            <div class="d-flex flex-row align-center">
              <v-text-field v-model="evalId" label="Conundrum ID" solo outlined class="mr-2" style="max-width: 320px"></v-text-field>
              <v-btn :loading="evalLoading" @click="loadEvalPrompt">Load Prompt</v-btn>
            </div>
            <v-textarea v-model="evalPrompt" solo outlined rows="12" class="mt-2"></v-textarea>
          </div>
        </v-sheet>
      </v-tab-item>
    </v-tabs-items>
  </div>
  </AdminLayout>
</template>

<script>
import { doc, setDoc, getDoc, collection, getDocs, query, where, updateDoc } from 'firebase/firestore'
import { db } from '@/firebase/init'
import AdminLayout from '@/layouts/Admin.vue'
export default {
  name: 'AdminConundrums',
  components: { AdminLayout },
  data() {
    return {
      progressValue: 100,
      tab: 0,
      providers: ['openai','gemini','claude','grok'],
      tiers: ['best','fast','mini'],

      // Dashboard
      topPrompts: [],
      topModels: [],
      processingCount: 0,
      allConundrums: [],
      allSearch: '',
      show: false,
      current: null,

      // Generate
      seedPrompt: '',
      genProvider: 'openai',
      genTier: 'fast',
      genCount: 1,
      generating: false,
      suggesting: false,
      unprocessed: [],
      unprocessedSearch: '',
      processingNow: false,

      // Review
      reviewItems: [],
      reviewSearch: '',
      reviewBottomTab: 0,
      currentReview: null,

      // Evaluation
      evalId: '',
      evalPrompt: '',
      evalLoading: false,
    }
  },
  computed: {
    isAdmin() {
      const email = (this.$root && this.$root.user && this.$root.user.email) || ''
      return email === 'aaronfarr@gmail.com' || email === 'socratesgx@gmail.com' || email === 'aaron@farrindustries.com'
    },
    allConundrumsFiltered() {
      const term = (this.allSearch||'').toLowerCase()
      const sorted = [...this.allConundrums].sort((a,b)=> (Number(b.rating||0) - Number(a.rating||0)) || ((b.evaluation?.score||0) - (a.evaluation?.score||0)))
      return sorted.filter(c => !term || (c.title||'').toLowerCase().includes(term) || (c.prompt||c.promptUsed||'').toLowerCase().includes(term))
    },
    unprocessedFiltered() {
      const term = (this.unprocessedSearch||'').toLowerCase()
      return this.unprocessed.filter(c => !term || (c.promptUsed||c.prompt||'').toLowerCase().includes(term))
    },
    reviewFiltered() {
      const term = (this.reviewSearch||'').toLowerCase()
      return this.reviewItems.filter(c => !term || (c.title||'').toLowerCase().includes(term) || (c.prompt||'').toLowerCase().includes(term))
    },
    unreviewedFiltered() {
      const list = this.reviewFiltered
      return list.filter(c => !c.rating || Number(c.rating) <= 0)
    },
    reviewedFiltered() {
      const list = this.reviewFiltered
      return list.filter(c => Number(c.rating||0) > 0)
    }
  },
  methods: {
    modelLabel(c) {
      const p = (c.model && c.model.provider) || c.generator || 'openai'
      const t = (c.model && c.model.tier) || 'fast'
      return `${p} - ${t}`
    },
    statusColor(s) {
      if (s === 'processing') return 'yellow darken-3'
      if (s === 'retry' || s === 'failed') return 'red'
      if (s === 'done') return 'green'
      return 'grey'
    },
    async loadDashboard() {
      const list = []
      try {
        const uid = this.$root?.user?.uid || ''
        if (this.isAdmin) {
          const snapAll = await getDocs(collection(db, 'conundrums'))
          snapAll.forEach(d => list.push({ id: d.id, ...d.data() }))
        } else {
          // Public
          const pubSnap = await getDocs(query(collection(db, 'conundrums'), where('state', '==', 'public')))
          pubSnap.forEach(d => list.push({ id: d.id, ...d.data() }))
          // Review
          const revSnap = await getDocs(query(collection(db, 'conundrums'), where('state', '==', 'review')))
          revSnap.forEach(d => list.push({ id: d.id, ...d.data() }))
          // My own (any state)
          if (uid) {
            const mineSnap = await getDocs(query(collection(db, 'conundrums'), where('creator.id','==', uid)))
            mineSnap.forEach(d => { if (!list.find(x=>x.id===d.id)) list.push({ id: d.id, ...d.data() }) })
          }
        }
      } catch (e) {
        console.error('loadDashboard error', e)
        this.$snackbar && this.$snackbar.error && this.$snackbar.error('Failed to load conundrums')
      }
      this.allConundrums = list
      // processing count
      this.processingCount = list.filter(c => ['unprocessed','processing','retry'].includes(c?.processing?.status)).length
      // top prompts by avg rating
      const mapP = {}
      list.forEach(c => {
        const key = (c.promptUsed || '')
        if (!key) return
        const r = Number(c.rating || 0)
        if (!mapP[key]) mapP[key] = { key, prompt: key, sum: 0, count: 0 }
        if (r > 0) { mapP[key].sum += r; mapP[key].count += 1 }
      })
      const arrP = Object.values(mapP).map(x => ({ ...x, avgRating: x.count ? x.sum / x.count : 0 }))
      this.topPrompts = arrP.filter(x=>x.count>=2).sort((a,b)=> (b.avgRating - a.avgRating) || (b.count - a.count)).slice(0,5)
      // top models by avg rating
      const mapM = {}
      list.forEach(c => {
        const p = (c.model && c.model.provider) || c.generator || 'openai'
        const t = (c.model && c.model.tier) || 'fast'
        const key = `${p}:${t}`
        const r = Number(c.rating || 0)
        if (!mapM[key]) mapM[key] = { key, label: `${p} - ${t}`, sum: 0, count: 0 }
        if (r > 0) { mapM[key].sum += r; mapM[key].count += 1 }
      })
      const arrM = Object.values(mapM).map(x => ({ ...x, avgRating: x.count ? x.sum / x.count : 0 }))
      this.topModels = arrM.filter(x=>x.count>=2).sort((a,b)=> (b.avgRating - a.avgRating) || (b.count - a.count)).slice(0,5)
    },
    async saveRating(c) {
      try {
        const r = Math.max(1, Math.min(5, Number(c.rating || 0)))
        await updateDoc(doc(db, 'conundrums', c.id), { rating: r, updatedAt: new Date() })
        c.rating = r
        this.$snackbar.success('Rating saved')
      } catch (e) { this.$snackbar.error(e.message) }
    },
    preview(c) { this.current = c; this.show = true },
    async publish(c) {
      try { await updateDoc(doc(db, 'conundrums', c.id), { state: 'public', updatedAt: new Date() }); c.state = 'public'; this.$snackbar.success('Published') } catch (e) { this.$snackbar.error(e.message) }
    },
    async reEvaluate(c) {
      try {
        const token = await this.$root.user.getIdToken()
        const res = await fetch(this.$endpoints.ideaverseInvoke, { method:'POST', headers: { 'Authorization':`Bearer ${token}`, 'Content-Type':'application/json' }, body: JSON.stringify({ agent:'astra', action:'reEvaluate', id: c.id }) })
        const json = await res.json(); if (!res.ok) throw new Error(json.error || 'Re-eval failed')
        this.$snackbar.success('Re-evaluated')
      } catch (e) { this.$snackbar.error(e.message) }
    },
    async rewrite(c) {
      try {
        const theme = prompt('Rewrite theme/story hint?') || ''
        const token = await this.$root.user.getIdToken()
        const res = await fetch(this.$endpoints.ideaverseInvoke, { method:'POST', headers: { 'Authorization':`Bearer ${token}`, 'Content-Type':'application/json' }, body: JSON.stringify({ agent:'astra', action:'rewrite', id: c.id, theme }) })
        const json = await res.json(); if (!res.ok) throw new Error(json.error || 'Rewrite failed')
        this.$snackbar.success('Rewritten')
        await this.loadDashboard(); await this.loadReview(); await this.loadUnprocessed()
      } catch (e) { this.$snackbar.error(e.message) }
    },

    // Generate
    async createUnprocessed() {
      this.generating = true
      try {
        const token = await this.$root.user.getIdToken()
        const res = await fetch(this.$endpoints.ideaverseInvoke, { method:'POST', headers:{ 'Authorization':`Bearer ${token}`, 'Content-Type':'application/json' }, body: JSON.stringify({ agent:'astra', action:'createUnprocessed', count: Number(this.genCount)||1, prompt: this.seedPrompt, provider: this.genProvider, tier: this.genTier }) })
        const json = await res.json(); if (!res.ok) throw new Error(json.error || 'Queue failed')
        this.$snackbar.success(`Queued ${json.created} conundrums`)
        this.loadUnprocessed(); this.loadDashboard()
      } catch (e) { this.$snackbar.error(e.message) }
      finally { this.generating = false }
    },
    async suggestSeed() {
      this.suggesting = true
      try {
        const token = await this.$root.user.getIdToken()
        const res = await fetch(this.$endpoints.ideaverseInvoke, { method:'POST', headers:{ 'Authorization':`Bearer ${token}`, 'Content-Type':'application/json' }, body: JSON.stringify({ agent:'astra', action:'suggestSeedPrompt' }) })
        const json = await res.json(); if (!res.ok) throw new Error(json.error || 'Suggest failed')
        this.seedPrompt = json.prompt || this.seedPrompt
      } catch (e) { this.$snackbar.error(e.message) }
      finally { this.suggesting = false }
    },
    async loadUnprocessed() {
      const items = []
      try {
        const uid = this.$root?.user?.uid || ''
        if (this.isAdmin) {
          const statuses = ['unprocessed','processing','retry']
          for (const s of statuses) {
            const snap = await getDocs(query(collection(db, 'conundrums'), where('processing.status','==', s)))
            snap.forEach(d => items.push({ id: d.id, ...d.data() }))
          }
        } else if (uid) {
          const snap = await getDocs(query(collection(db, 'conundrums'), where('creator.id','==', uid)))
          snap.forEach(d => {
            const data = d.data() || {}
            const st = (data.processing && data.processing.status) || ''
            if (st === 'unprocessed' || st === 'processing' || st === 'retry') items.push({ id: d.id, ...data })
          })
        }
      } catch (e) {
        console.error('loadUnprocessed error', e)
      }
      this.unprocessed = items
    },
    async processUnprocessedNow() {
      this.processingNow = true
      try {
        const token = await this.$root.user.getIdToken()
        const res = await fetch(this.$endpoints.ideaverseInvoke, { method:'POST', headers:{ 'Authorization':`Bearer ${token}`, 'Content-Type':'application/json' }, body: JSON.stringify({ agent:'astra', action:'processUnprocessedOnce', limit: 5 }) })
        const json = await res.json(); if (!res.ok) throw new Error(json.error || 'Processing failed')
        this.$snackbar.success(`Processed ${json.processed||0}`)
        this.loadUnprocessed(); this.loadDashboard(); this.loadReview()
      } catch (e) { this.$snackbar.error(e.message) }
      finally { this.processingNow = false }
    },

    // Review
    async loadReview() {
      try {
        const snap = await getDocs(query(collection(db, 'conundrums'), where('state','==','review')))
        this.reviewItems = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        // set a default current if needed
        if (!this.currentReview) this.currentReview = this.unreviewedFiltered[0] || this.reviewFiltered[0] || null
      } catch (e) {
        console.error('loadReview error', e)
        this.reviewItems = []
      }
    },
    setCurrent(c) { this.currentReview = c },
    async rateCurrent(n) {
      if (!this.currentReview) return
      const c = this.currentReview
      c.rating = Number(n)
      await this.saveRating(c)
      // advance to next unreviewed
      const idx = this.unreviewedFiltered.findIndex(x => x.id === c.id)
      const next = this.unreviewedFiltered.find((x, i) => i > idx)
      this.currentReview = next || this.unreviewedFiltered[0] || null
    },
    skipCurrent() {
      if (!this.currentReview) return
      const c = this.currentReview
      const idx = this.unreviewedFiltered.findIndex(x => x.id === c.id)
      const next = this.unreviewedFiltered.find((x, i) => i > idx)
      this.currentReview = next || this.unreviewedFiltered[0] || null
    },

    // Evaluation tab
    async loadEvalPrompt() {
      this.evalLoading = true
      try {
        const token = await this.$root.user.getIdToken()
        const res = await fetch(this.$endpoints.ideaverseInvoke, { method:'POST', headers:{ 'Authorization':`Bearer ${token}`, 'Content-Type':'application/json' }, body: JSON.stringify({ agent:'astra', action:'getEvaluationPrompt', id: this.evalId }) })
        const json = await res.json(); if (!res.ok) throw new Error(json.error || 'Failed to load prompt')
        this.evalPrompt = json.prompt || ''
      } catch (e) { this.$snackbar.error(e.message) }
      finally { this.evalLoading = false }
    },
  },
  mounted() {
    const runLoads = () => { this.loadDashboard(); if (this.isAdmin) this.loadUnprocessed(); this.loadReview() }
    if (this.$root && this.$root.user) {
      runLoads()
    } else {
      const timer = setInterval(() => {
        if (this.$root && this.$root.user) { clearInterval(timer); runLoads() }
      }, 300)
      // safety stop after 10s
      setTimeout(() => clearInterval(timer), 10000)
    }
  },
}
</script>